import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiClientConfig } from '../../api-client-config';
import { LegalEntity } from '@ms-organization-entities';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';

import {
  CommonDocumentEditorDetails,
  DocRenameRequest,
  DocumentDuplicateResponse,
  DocumentLockResponse,
  ItemDocDependency,
  RiskAssessmentOverrideOnDuplicate,
} from '@primatoo-ms-organization';
import { map } from 'rxjs/operators';
import { ApiClientUtils } from '../../../api-client.utils';
import { BusinessProjectManagementService } from '../../interface/business-project-management-service';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { ListTemplateChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse';
import { DocResourceStatus } from 'it/primatoo/openapi/api/org/schemas/common/DocResourceStatus';
import { ListDocumentResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListDocumentResponse';
import { CreateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import { GetGlobalMeasCheckPermissionResponse } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/GetGlobalMeasCheckPermissionResponse';
import { UpdateGlobalMeasCheckPermissionRequest } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/UpdateGlobalMeasCheckPermissionRequest';
import { GetGlobalMeasCheckAvailableOperationsResponse } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/GetGlobalMeasCheckAvailableOperationsResponse';
import { DocumentItem } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listdocumentresponse/DocumentItem';
import { GetGlobalMeasCheckForDetailsResponse } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheck/GetGlobalMeasCheckForDetailsResponse';
import { GetGlobalMeasCheckForEditorDetailResponse } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheck/GetGlobalMeasCheckForEditorDetailResponse';
import { CreateGlobalMeasureDocRequest } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/CreateGlobalMeasureDocRequest';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';
import { UpdateGlobalMeasureDocRequest } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/UpdateGlobalMeasureDocRequest';
import { GlobalMeasureCheckDependency } from '@pt/pt-models';
import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';

@Injectable({
  providedIn: 'root',
})
export class GlobalMeasureChecksService extends BusinessProjectManagementService {
  private readonly docsEndpoint: string;
  private readonly docsV2Endpoint: string;
  private readonly endpointV1: string;
  private readonly endpointV2: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient
  ) {
    super(httpClient);
    this.endpointV1 = this.config.endPoint + '/api/org/global-measure-checks/';
    this.endpointV2 =
      this.config.endPoint + '/api/org/v2/global-measure-checks/';
    this.docsEndpoint =
      this.config.endPoint + '/api/org/docs/global-measure-checks/';
    this.docsV2Endpoint =
      this.config.endPoint + '/api/org/v2/docs/global-measure-checks/';
  }

  getEndPointV1(): string {
    return this.endpointV1;
  }

  getEndPointV2(): string {
    return this.endpointV2;
  }

  // ----------------------------------- API DOC V2 ----------------------------------- //
  getActivePermission(
    docId: number
  ): Observable<GetGlobalMeasCheckPermissionResponse> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.get<GetGlobalMeasCheckPermissionResponse>(url);
  }

  patchActivePermission(
    docId: number,
    request: UpdateGlobalMeasCheckPermissionRequest
  ): Observable<void> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.patch<void>(url, request);
  }

  // ----------------------------------- API V2 ----------------------------------- //
  getTreatmentTemplateChapterId(
    id: number,
    chapterId: number
  ): Observable<GetChapterResponse> {
    return this.httpClient.get<GetChapterResponse>(
      this.endpointV2 + `${id}/latest/template/chapters/${chapterId}/`
    );
  }

  getDocumentTemplateChapters(
    id: number
  ): Observable<ListTemplateChapterResponse> {
    return this.httpClient.get<ListTemplateChapterResponse>(
      this.endpointV2 + `${id}/latest/template/chapters/`
    );
  }

  findAllFiltered(
    filter?: string,
    filterByAnyPermission?: boolean,
    columns?: string[],
    selectedLegalEntities?: LegalEntity[]
  ): Observable<Array<DocumentItem>> {
    let url = this.endpointV2 + `latest/`;
    url += filter ? '?q=' + filter : '';

    if (selectedLegalEntities?.length > 0) {
      if (!columns.includes('status')) {
        columns.push('status');
      }
      if (!columns.includes('legalEntitiesIds')) {
        columns.push('legalEntitiesIds');
      }
      if (filter) {
        url += `,${ApiClientUtils.buildLegalEntitiesFilter(
          selectedLegalEntities,
          'legalEntitiesIds'
        )}`;
      } else {
        url += `?q=${ApiClientUtils.buildLegalEntitiesFilter(
          selectedLegalEntities,
          'legalEntitiesIds'
        )}`;
      }
    }

    // if (selectedLegalEntities?.length > 0) {
    //   if (filter) {
    //    url += `,${ApiClientUtils.buildLegalEntitiesFilter(selectedLegalEntities, 'legalEntitiesIds')}`;
    //   } else {
    //     url += `?q=${ApiClientUtils.buildLegalEntitiesFilter(selectedLegalEntities, 'legalEntitiesIds')}`;
    //   }
    // }

    url +=
      filter || selectedLegalEntities?.length > 0
        ? '&sort=docId,DESC'
        : '?sort=docId,DESC';
    url += filterByAnyPermission
      ? '&filterByAnyPermission=true'
      : '&filterByAnyPermission=false';
    if (columns && columns.length > 0) {
      url += '&columns=' + columns.join('%7C');
    }

    return this.httpClient.get<ListDocumentResponse>(url).pipe(
      map((response) => {
        return response.documents; //.sort((a: any, b: any) => a.docId > b.docId ? 1 : -1);
      })
    );
  }

  // ----------------------------------- API V1 ----------------------------------- //

  getDocColumn(): Observable<ColumnWithLocalization[]> {
    const url = this.docsEndpoint + `columns/`;
    return this.httpClient.get<ColumnWithLocalization[]>(url);
  }

  create(body: CreateGlobalMeasureDocRequest): Observable<CreateDocResponse> {
    const url = this.docsV2Endpoint;
    return this.httpClient.post<CreateDocResponse>(url, body);
  }

  delete(id: number): Observable<void> {
    const url = this.docsEndpoint + `${id}/`;
    return this.httpClient.delete<void>(url);
  }

  update(
    id: number,
    body: UpdateGlobalMeasureDocRequest
  ): Observable<UpdateDocResponse> {
    const url = this.docsV2Endpoint + `${id}/`;
    return this.httpClient.put<UpdateDocResponse>(url, body);
  }

  rename(id: number, request: DocRenameRequest): Observable<void> {
    const url = this.docsEndpoint + `${id}/rename`;
    return this.httpClient.patch<void>(url, request);
  }

  getToOverride(): Observable<RiskAssessmentOverrideOnDuplicate> {
    const url = this.endpointV1 + `override-on-duplicate`;
    return this.httpClient.get<RiskAssessmentOverrideOnDuplicate>(url);
  }

  duplicate(
    id: number,
    request: CreateGlobalMeasureDocRequest
  ): Observable<DocumentDuplicateResponse> {
    const url = this.endpointV2 + `${id}/latest/duplicate`;
    return this.httpClient.post<DocumentDuplicateResponse>(url, request);
  }

  reopen(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/re-open`;
    return this.httpClient.post<void>(url, {});
  }

  getDetails(id: number): Observable<GetGlobalMeasCheckForDetailsResponse> {
    const url = this.endpointV2 + `${id}/latest/details`;
    return this.httpClient.get<GetGlobalMeasCheckForDetailsResponse>(url);
  }

  getEditorDetails(id: number): Observable<CommonDocumentEditorDetails> {
    const url = this.endpointV2 + `${id}/latest/editor-details/`;
    return this.httpClient
      .get<GetGlobalMeasCheckForEditorDetailResponse>(url)
      .pipe(
        map((m) => {
          const response: CommonDocumentEditorDetails = {
            docId: m.docId,
            creatorUserId: m.creatorUserId,
            status: m.status as DocResourceStatus,
            statusLabel: m.statusLabel,
            name: m.name,
            warnings: [],
            userCheck: m.userCheck,
            description: m.description,
            internalId: m.internalId,
            legalEntities: m.legalEntities,
            parentId: m.parentId,
            parentName: m.parentName,
            updatedAt: m.updatedAt,
            completed: m.completed,
          };
          return response;
        })
      );
  }

  getDependency(id: number): Observable<GlobalMeasureCheckDependency> {
    const url = this.endpointV1 + `${id}/latest/dependency`;
    return this.httpClient.get<GlobalMeasureCheckDependency>(url);
  }

  upsertWidgets(id: number, widgets: UpsertWidgetsRequest): Observable<void> {
    const url = this.endpointV2 + `${id}/latest/widgets/`;
    return this.httpClient.post<void>(url, widgets);
  }

  releaseLock(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/lock`;
    return this.httpClient.delete<void>(url, {});
  }

  lockResource(id: number, token: number): Observable<DocumentLockResponse> {
    let url = this.endpointV1 + `${id}/latest/lock`;
    if (token !== null) {
      url = `${url}?token=${token}`;
    }
    return this.httpClient.post<DocumentLockResponse>(url, {});
  }

  publish(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/publish`;
    return this.httpClient.post<void>(url, {});
  }

  refreshResults(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/refresh-results`;
    return this.httpClient.post<void>(url, {});
  }

  deleteMultiple(ids: number[]): Observable<{ failed: number[] }> {
    const url = this.docsEndpoint + `by-ids`;
    // return this.httpClient.request<{ failed: number[] }>('DELETE', url, {body: {ids}})
    return this.httpClient.post<{ failed: number[] }>(url, { ids });
  }

  userChecked(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/user-checked`;
    return this.httpClient.post<void>(url, {});
  }

  preview(id: number) {
    const url = this.downloadPreviewPdfUrl(id);
    return this.httpClient.get(url, { responseType: 'text' });
  }

  getDocUserPermission(
    docId: number
  ): Observable<GetGlobalMeasCheckAvailableOperationsResponse> {
    const url = this.endpointV2 + `${docId}/latest/user-operations/`;
    return this.httpClient.get<GetGlobalMeasCheckAvailableOperationsResponse>(
      url
    );
  }

  getDocDependencies(id: number): Observable<ItemDocDependency[]> {
    const url = this.endpointV1 + `${id}/latest/doc-dependency/`;
    return this.httpClient.get<ItemDocDependency[]>(url);
  }
}

